import { module } from 'modujs';
import vertexShader from '../shaders/vertex';
import fragmentShader from '../shaders/fragment';
import antiAliasFragmentShader from '../shaders/antiAliasFragment';
import { lerp } from '../utils/maths';

import {Renderer, Camera, Transform, Texture, Post, Color, Sphere, Orbit, Vec3, Vec2, Program, Mesh } from 'ogl';

const CLASS = {
    LOADING: 'is-loading'
}

export default class extends module {
    constructor(m) {
        super(m);

        this.el.classList.add(CLASS.LOADING);

        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;

        // El and webgl plane BCR
        this.BCR = this.el.getBoundingClientRect();
        this.meshBCR = {
            width: 1,
            height: 1,
            x: 0,
            y: 0
        }

        this.events = {
            mouseenter: {
                wrap: 'play'
            },
            mouseleave: {
                wrap: 'pause'
            }
        }

        // Useful booleans
        this.isLoaded = false;
        this.isRenderable = false;

        this.start = Date.now();
        this.factor = {
            value: 0,
            lerped: 0
        }

    }

    init() {

        this.$wrap = this.$('wrap')[0];

        // Init ogl renderer
        this.renderer = new Renderer({dpr: 1, antialias: true, webgl: 1, alpha: true});
        this.renderer.setSize(this.BCR.width, this.BCR.height);

        this.gl = this.renderer.gl;
        this.$wrap.appendChild(this.gl.canvas);
        let clearColor = new Color(this.getData('clear-color'));
        // console.log(clearColor);
        this.gl.clearColor(clearColor[0],clearColor[1],clearColor[2],1);

        // Post copies the current renderer values (width, height, dpr) if none are passed in
        this.post = new Post(this.gl);

        // Create uniform for pass
        this.resolution = {value: new Vec2(2,2)};

        // Add pass like you're creating a Program. Then use the 'enabled'
        // property to toggle the pass.
        const pass = this.post.addPass({

            // If not passed in, pass will use the default vertex/fragment
            // shaders found within the class.
            fragment: antiAliasFragmentShader,
            uniforms: {
                uResolution: this.resolution
            },
        });

        this.initScene();
        this.initCamera();
        this.initShape();

        this.resizeBind = this.resize.bind(this);
        window.addEventListener('resize', this.resizeBind);
    }

    initScene() {
        this.scene = new Transform();
    }

    initCamera() {
        this.fov = 75;
        this.camera = new Camera(this.glElement, {fov: this.fov});
        this.camera.position.set(0,0,40);

        // this.controls = new Orbit(this.camera, {
        //     target: new Vec3(0, 0, 0),
        //     element: this.el,
        //     // autoRotate: true,
        //     enableZoom: false
        // });
    }

    initShape() {

        this.geometry = new Sphere(this.gl,{radius: 200, widthSegments: 64, heightSegments: 64});

        // const matCap = new Texture(this.gl, {minFilter: this.gl.LINEAR});
        // const matCapSrc = '/assets/images/gl/matcap5.jpg';

        const texture = new Texture(this.gl, {
            minFilter: this.gl.LINEAR,
            anisotropy: 16
        });
        const textureSrc = '/assets/images/gl/texture.jpg';

        const img = new Image();
        img.src = textureSrc;
        img.onload = () => {
            texture.image = img;

            // const mat = new Image();
            // mat.src = matCapSrc;
            // mat.onload = () => {
            //     matCap.image = mat;

                this.program = new Program(this.gl, {
                    vertex: vertexShader,
                    fragment: fragmentShader,
                    uniforms: {
                        time: {value: 0},
                        factor: {value: this.factor.lerped},
                        tMap: {value: texture},
                        // matcapTexture: {value: matCap}
                    },
                    transparent: true,
                    cullFace: null
                });


                this.mesh = new Mesh(this.gl, {geometry: this.geometry, program: this.program});

                this.updateSize();
                this.isLoaded = true;
                this.el.classList.remove(CLASS.LOADING);
                this.render();

            // }

        }
    }

    play() {
        cancelAnimationFrame(this.raf);

        this.factor.value = 0.8;
        this.isRenderable = true;
        this.render();
    }
    pause() {
        this.factor.value = 0;
    }

    calculateUnitSize(distance){
        const vFov = this.fov * Math.PI / 180;
        const height = (2 * Math.tan(vFov / 2) * distance);
        const width = height * this.camera.aspect;

        return {
            width,
            height
        }
    }

    updateSize() {
        this.camUnit = this.calculateUnitSize(this.camera.position.z);

        // Set size @update
        this.meshBCR.width = this.camUnit.width;
        this.meshBCR.height = this.meshBCR.width / this.camera.aspect;

        this.geometry = new Sphere(this.gl,{radius: 20, widthSegments: 100, heightSegments: 100});
        this.mesh.geometry = this.geometry;
        this.mesh.rotation.z = -Math.PI/4;

        // this.mesh.scale.set(0.001,0.001,0.001);

        this.gl.canvas.style.width = `${this.BCR.width}px`;
        this.gl.canvas.style.height = `${this.BCR.height}px`;

        this.resolution.value.set(this.gl.canvas.width, this.gl.canvas.height);

    }

    formatPosition(param) {
        param.obj.x = -(param.x) + 0.5;
        param.obj.y = param.y - 0.5
    }

    render(t) {
        this.raf = requestAnimationFrame((t)=>this.render(t));

        if(this.isLoaded && this.isRenderable) {
            // this.mesh.rotation.set(t * 0.0001, t * 0.00005, - t * 0.0001);
            this.factor.lerped = lerp(this.factor.lerped, this.factor.value, 0.03) ;

            this.program.uniforms.time.value = .0001 * ( Date.now() - this.start );
            this.program.uniforms.factor.value = Math.ceil(this.factor.lerped * 100) / 100;

            if(this.program.uniforms.factor.value < 0.02) {
                this.isRenderable = false;
                cancelAnimationFrame(this.raf);
            }

        }

        // this.controls.update();
        this.post.render({scene: this.mesh, camera: this.camera});
        // this.renderer.render({scene: this.mesh, camera: this.camera});

    }

    resize() {
        const newBCR = this.el.getBoundingClientRect()
        if(this.BCR && this.BCR.top == newBCR.top && this.BCR.height == newBCR.height) return
        this.BCR = newBCR

        this.windowWidth = window.innerWidth;
        this.windowHeight = window.innerHeight;

        this.renderer.setSize(this.BCR.width, this.BCR.height);
        this.camera.perspective({aspect: this.gl.canvas.width / this.gl.canvas.height});
        this.updateSize();

         // Need to resize post as render targets need to be re-created
         this.post.resize();
    }

    destroy() {
        super.destroy();
        cancelAnimationFrame(this.raf);
        window.removeEventListener('resize', this.resizeBind);
    }
}
